import React, { useState } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField, 
  Box, 
  Grid, 
  Typography, 
  IconButton,
  Divider,
  styled,
  Card,
  Alert,
  CircularProgress,
  Chip,
  Stack
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TripLocationPicker from './TripLocationPicker';
import { LocationResult } from '../../types/Location';
import AccommodationForm from './AccommodationForm';
import { Accommodation } from '../../types/Trip';
import CoverImageUpload from './CoverImageUpload';
import { enhanceTripDescription } from '../../services/tripAIService';

// Define trip data interface
interface TripData {
   title: string;
   description: string;
   startDate: Date | null;
   endDate: Date | null;
   destination?: LocationResult | null;
   coverImage: string | null;
   accommodations: Accommodation[];
   tags?: string[];
}

interface CreateTripFormProps {
   open: boolean;
   onClose: () => void;
   onSave: (tripData: any) => void;
}

const UploadButton = styled(Button)(({ theme }) => ({
  border: `1px dashed ${theme.palette.divider}`,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.default,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  }
}));

const CreateTripForm: React.FC<CreateTripFormProps> = ({ open, onClose, onSave }) => {
  // Initialize trip data state
  const [tripData, setTripData] = useState<TripData>({
    title: '',
    description: '',
    startDate: new Date(),
    endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // Default to 1 week later
    destination: null,
    coverImage: null,
    accommodations: [],
    tags: []
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enhancing, setEnhancing] = useState(false);
  const [suggestedTags, setSuggestedTags] = useState<string[]>([]);
  const [suggestedHighlights, setSuggestedHighlights] = useState<string[]>([]);
  const [suggestedActivities, setSuggestedActivities] = useState<string[]>([]);
  const [packingTips, setPackingTips] = useState<string[]>([]);

  // Handle form submission
  const handleSubmit = () => {
    // Check if we have the minimum required data
    if (!tripData.startDate || !tripData.endDate || !tripData.title.trim()) {
      setError('Please enter a trip title and select start and end dates');
      return;
    }

    // Save trip data
    onSave(tripData);
    
    // Close the dialog
    onClose();
  };

  // Handle destination selection
  const handleDestinationSelect = (location: LocationResult | null) => {
    setTripData({
      ...tripData,
      destination: location
    });
  };

  // Handle accommodations update
  const handleAccommodationsChange = (accommodations: Accommodation[]) => {
    setTripData({
      ...tripData,
      accommodations
    });
  };

  // Handle AI enhancement
  const handleEnhanceWithAI = async () => {
    if (!tripData.title) {
      setError('Please enter a basic title first');
      return;
    }

    try {
      setEnhancing(true);
      setError(null);

      const enhancement = await enhanceTripDescription({
        title: tripData.title,
        description: tripData.description,
        startDate: tripData.startDate || new Date(),
        endDate: tripData.endDate || new Date(),
        destination: tripData.destination
      });

      // Update form with enhanced content
      setTripData(prev => ({
        ...prev,
        title: enhancement.suggestedTitle || prev.title,
        description: enhancement.enhancedDescription,
        tags: [...(prev.tags || []), ...enhancement.suggestedTags]
      }));

      setSuggestedTags(enhancement.suggestedTags);
      setSuggestedHighlights(enhancement.suggestedHighlights);
      setSuggestedActivities(enhancement.suggestedActivities);
      setPackingTips(enhancement.packingTips);

    } catch (err) {
      console.error('Error enhancing trip description:', err);
      setError('Failed to enhance trip description. Please try again.');
    } finally {
      setEnhancing(false);
    }
  };

  // Handle tag management
  const handleAddTag = (tag: string) => {
    if (!tripData.tags?.includes(tag)) {
      setTripData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), tag]
      }));
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTripData(prev => ({
      ...prev,
      tags: prev.tags?.filter(tag => tag !== tagToRemove) || []
    }));
  };

  // Add handleImageUpload function back
  const handleImageUpload = async (file: File) => {
    try {
      setLoading(true);
      
      // Create a temporary URL for the selected file
      const imageUrl = URL.createObjectURL(file);
      
      // Update trip data with the image URL
      setTripData({
        ...tripData,
        coverImage: imageUrl
      });
      
      setLoading(false);
    } catch (error) {
      console.error('Error uploading image:', error);
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Create New Trip</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          {/* AI Enhancement Card */}
          <Card sx={{ mb: 3, p: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <AutoAwesomeIcon />
              <Typography variant="h6">Get Trip Suggestions with AI</Typography>
            </Box>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Let AI help you plan your trip with personalized suggestions for activities, packing tips, and more.
            </Typography>
            <Button
              variant="contained"
              onClick={handleEnhanceWithAI}
              disabled={enhancing || !tripData.title}
              startIcon={enhancing ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
              sx={{ bgcolor: 'background.paper', color: 'primary.main' }}
            >
              {enhancing ? 'Generating Suggestions...' : 'Get AI Suggestions'}
            </Button>
          </Card>

          <Grid container spacing={3}>
            {/* Trip Title */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Trip Title *
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter trip title"
                value={tripData.title}
                onChange={(e) => setTripData({...tripData, title: e.target.value})}
                required
              />
            </Grid>
            
            {/* Date selection row */}
            <Grid item xs={12} container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Start Date *
                </Typography>
                <DatePicker 
                  value={tripData.startDate} 
                  onChange={(date) => setTripData({...tripData, startDate: date})}
                  renderInput={params => (
                    <TextField {...params} fullWidth />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" gutterBottom>
                  End Date *
                </Typography>
                <DatePicker 
                  value={tripData.endDate} 
                  onChange={(date) => setTripData({...tripData, endDate: date})}
                  renderInput={params => (
                    <TextField {...params} fullWidth />
                  )}
                />
              </Grid>
            </Grid>
            
            {/* Destination picker */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <TripLocationPicker 
                onSelectLocation={handleDestinationSelect}
                selectedLocation={tripData.destination}
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Trip Description
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={6}
                value={tripData.description}
                onChange={(e) => setTripData({...tripData, description: e.target.value})}
                placeholder="Describe your trip plans..."
              />
            </Grid>

            {/* AI Suggestions */}
            {(suggestedHighlights.length > 0 || suggestedActivities.length > 0 || packingTips.length > 0) && (
              <Grid item xs={12}>
                <Box sx={{ mt: 2 }}>
                  {suggestedHighlights.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        ✨ Suggested Highlights
                      </Typography>
                      <ul>
                        {suggestedHighlights.map((highlight, index) => (
                          <li key={index}>{highlight}</li>
                        ))}
                      </ul>
                    </Box>
                  )}

                  {suggestedActivities.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        🎯 Suggested Activities
                      </Typography>
                      <ul>
                        {suggestedActivities.map((activity, index) => (
                          <li key={index}>{activity}</li>
                        ))}
                      </ul>
                    </Box>
                  )}

                  {packingTips.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        🎒 Packing Tips
                      </Typography>
                      <ul>
                        {packingTips.map((tip, index) => (
                          <li key={index}>{tip}</li>
                        ))}
                      </ul>
                    </Box>
                  )}
                </Box>
              </Grid>
            )}
            
            {/* Tags */}
            {suggestedTags.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Suggested Tags
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                  {suggestedTags.map((tag, index) => (
                    <Chip
                      key={index}
                      label={tag}
                      onClick={() => handleAddTag(tag)}
                      color="primary"
                      variant={tripData.tags?.includes(tag) ? "filled" : "outlined"}
                      onDelete={
                        tripData.tags?.includes(tag) 
                          ? () => handleRemoveTag(tag)
                          : undefined
                      }
                    />
                  ))}
                </Stack>
              </Grid>
            )}
            
            {/* Accommodations */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <AccommodationForm 
                accommodations={tripData.accommodations}
                onChange={handleAccommodationsChange}
                tripStartDate={tripData.startDate}
                tripEndDate={tripData.endDate}
              />
            </Grid>
            
            {/* Cover image upload */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle1" gutterBottom>
                Trip Cover Image
              </Typography>
              <CoverImageUpload
                coverPhotoUrl={tripData.coverImage || ''}
                onImageChange={(url) => setTripData({ ...tripData, coverImage: url })}
                onImageUpload={handleImageUpload}
                loading={loading}
                title="Trip Cover"
                height={200}
              />
            </Grid>
          </Grid>
        </LocalizationProvider>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Button 
          color="inherit" 
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleSubmit}
          disabled={!tripData.startDate || !tripData.endDate || !tripData.title.trim()}
        >
          Create Trip
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateTripForm; 